import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { getPasscode, setPasscode } from './api';
import './SoccerFormation.css';

const SHAPES = {
  "4-3-3": [[0.05,0.5],[0.22,0.14],[0.22,0.38],[0.22,0.62],[0.22,0.86],[0.45,0.26],[0.45,0.5],[0.45,0.74],[0.72,0.2],[0.75,0.5],[0.72,0.8]],
  "4-4-2": [[0.05,0.5],[0.22,0.14],[0.22,0.38],[0.22,0.62],[0.22,0.86],[0.46,0.14],[0.46,0.38],[0.46,0.62],[0.46,0.86],[0.72,0.38],[0.72,0.62]],
  "4-2-3-1": [[0.05,0.5],[0.22,0.14],[0.22,0.38],[0.22,0.62],[0.22,0.86],[0.4,0.36],[0.4,0.64],[0.6,0.16],[0.6,0.5],[0.6,0.84],[0.8,0.5]],
};

const POSITIONS = ["GK", "DEF", "MID", "FWD"];
const CAP = 11;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function dateLabel(d) {
  if (!d) return "No game";
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

function todayPacific() {
  // The board is shared between captains, so the day rolls over on Pacific
  // time for everyone, not on whatever clock the phone is set to.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

class SoccerFormation extends React.Component {
  history = [];
  field = null;
  fieldWrap = null;
  tokenDown = false;
  origin = { x: 0, y: 0 };
  saveTimer = null;
  rosterTimers = {};
  filterInput = null;
  serverAt = null;
  rosterAt = 0;
  polling = false;
  pickStart = null;
  retryAction = null;

  state = this.initialState();

  freshState() {
    return {
      players: [],
      opponents: [], showOpp: false, picking: false, query: "",
      running: false, elapsed: 0, startedAt: null, period: 1,
      formation: "4-2-3-1", tab: "bench", log: [],
      selectedId: null, selectedBenchId: null, hoverId: null, canUndo: false,
      snap: true,
      dragId: null, dragFrom: null, dragGroup: false, dragX: 0, dragY: 0, moved: false, lifted: false, confirmReset: false,
      formationOpen: false,
    };
  }

  initialState() {
    return {
      ...this.freshState(),
      isPortrait: typeof window !== "undefined" && (window.innerWidth < 900 || window.innerHeight > window.innerWidth * 1.05),
      wrapW: 0, wrapH: 0,
      roster: [],
      date: null, games: [], gamesOpen: false, newDate: todayPacific(),
      loading: true, loadError: null, saveState: "idle", justSynced: false,
      needPasscode: false, passcodeInput: getPasscode(),
    };
  }

  /* ------------------------------------------------------------- loading */

  byNumber(a, b) {
    return (Number(a.num) || 0) - (Number(b.num) || 0) || a.id - b.id;
  }

  // The squad is only the players called up for this game. A player who has
  // since left the roster keeps the name and number they played under.
  buildSquad(roster, played) {
    const byId = new Map(roster.map(p => [p.id, p]));
    return played.map(g => {
      const r = byId.get(g.id);
      return {
        id: g.id,
        num: r ? r.num : g.num, name: r ? r.name : g.name, pos: r ? r.pos : g.pos,
        onPitch: g.onPitch, u: g.u, v: g.v, secs: g.secs, seq: g.seq || 0,
      };
    }).sort(this.byNumber);
  }

  toggleSquad(id) {
    this.mark();
    this.setState(s => {
      const inSquad = s.players.find(p => p.id === id);
      if (inSquad) {
        // Dropping someone who has played would throw away their minutes.
        if (inSquad.onPitch || inSquad.secs > 0) return null;
        return { players: s.players.filter(p => p.id !== id) };
      }
      const r = s.roster.find(p => p.id === id);
      if (!r) return null;
      return {
        players: s.players
          .concat([{ id: r.id, num: r.num, name: r.name, pos: r.pos, onPitch: false, u: 0.5, v: 0.5, secs: 0, seq: 0 }])
          .sort(this.byNumber),
      };
    }, () => this.save());
  }

  // Shared by the first load and by a change picked up from another device.
  gameFields(g, players) {
    const out = {
      players,
      opponents: g.opponents || [], showOpp: !!g.showOpp, running: !!g.running,
      elapsed: g.elapsed || 0, startedAt: g.startedAt || null, period: g.period || 1,
      formation: SHAPES[g.formation] ? g.formation : "4-2-3-1", snap: g.snap !== false,
    };
    // The clock kept running while this page was not the one writing.
    if (out.running && g.updatedAt) {
      const cap = out.period === 1 ? 2700 : 5400;
      const d = Math.min(Math.max(Math.round((Date.now() - g.updatedAt) / 1000), 0), 7200);
      const elapsed = Math.min(out.elapsed + d, cap);
      const gained = elapsed - out.elapsed;
      out.elapsed = elapsed;
      out.running = elapsed < cap;
      out.players = out.players.map(p => p.onPitch ? { ...p, secs: p.secs + gained } : p);
    }
    return out;
  }

  load(date) {
    this.setState({ loading: true, loadError: null });
    const wanted = date || this.props.date || null;
    Promise.all([
      api.players(),
      (wanted ? api.game(wanted) : api.currentGame())
        .catch(e => { if (/No game/i.test(e.message)) return { game: null, players: [], events: [] }; throw e; }),
      api.games().catch(() => ({ games: [] })),
    ]).then(([r, state, list]) => {
      const g = state.game;
      const base = {
        ...this.freshState(),
        players: this.buildSquad(r.players, state.players),
        log: state.events || [],
      };
      if (g) Object.assign(base, this.gameFields(g, base.players));
      this.serverAt = g ? g.updatedAt : null;
      this.history = [];
      this.setState({
        ...base,
        tab: this.state.tab,
        roster: r.players,
        date: g ? g.id : null,
        games: list.games || [],
        loading: false, loadError: null, canUndo: false, saveState: "idle",
      });
    }).catch(e => this.setState({ loading: false, loadError: e.message }));
  }

  // Another captain may be editing the same game. Check often enough to keep
  // up, and never overwrite work in progress on this device.
  busy() {
    const s = this.state;
    return s.dragId != null || s.saveState === "pending" || s.saveState === "saving";
  }

  pickDown(e, row) {
    this.pickStart = { id: row.id, x: e.clientX, y: e.clientY };
    // Keeping the caret in the filter is a mouse convenience; preventing the
    // default on touch would stop the list scrolling.
    if (e.pointerType === "mouse" && e.preventDefault) e.preventDefault();
  }

  pickUp(e, row) {
    const start = this.pickStart;
    this.pickStart = null;
    if (!start || start.id !== row.id) return;
    const dx = Math.abs(e.clientX - start.x), dy = Math.abs(e.clientY - start.y);
    if (dx + dy > 7) return;
    row.act();
    if (e.pointerType === "mouse" && this.filterInput) this.filterInput.focus();
  }

  poll() {
    const s = this.state;
    if (!s.date || s.loading || this.polling || this.busy()) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    const date = s.date;
    this.polling = true;
    api.version(date)
      .then(v => {
        const gameMoved = v.updatedAt && v.updatedAt !== this.serverAt;
        const rosterMoved = v.rosterAt && v.rosterAt !== this.rosterAt;
        if (!gameMoved && !rosterMoved) return null;
        return this.applyRemote(date, v, gameMoved, rosterMoved);
      })
      .catch(() => {})
      .then(() => { this.polling = false; }, () => { this.polling = false; });
  }

  applyRemote(date, v, gameMoved, rosterMoved) {
    return Promise.all([
      gameMoved ? api.game(date) : null,
      rosterMoved ? api.players() : null,
    ]).then(([state, r]) => {
      // A drag or an edit can begin while those requests are in flight.
      if (this.state.date !== date || this.busy()) return;
      const roster = r ? r.players : this.state.roster;
      const next = { justSynced: true };
      if (r) { next.roster = roster; this.rosterAt = v.rosterAt; }
      if (state) {
        const squad = this.buildSquad(roster, state.players);
        Object.assign(next, this.gameFields(state.game, squad));
        next.log = state.events || [];
        this.serverAt = state.game.updatedAt;
      } else if (r) {
        // Only names or numbers changed; keep this game's positions.
        const byId = new Map(roster.map(p => [p.id, p]));
        next.players = this.state.players.map(p => {
          const x = byId.get(p.id);
          return x ? { ...p, num: x.num, name: x.name, pos: x.pos } : p;
        });
      }
      // Undo would now restore positions from before someone else's change.
      this.history = [];
      next.canUndo = false;
      next.selectedId = null;
      next.selectedBenchId = null;
      this.setState(next, () => {
        clearTimeout(this.syncFlag);
        this.syncFlag = setTimeout(() => this.setState({ justSynced: false }), 4000);
      });
    });
  }

  go(date) {
    if (this.props.onNavigate && date !== this.props.date) this.props.onNavigate(date);
    else this.load(date);
    this.setState({ gamesOpen: false });
  }

  createGame(date) {
    const d = date || this.state.newDate;
    if (!DATE_RE.test(d)) return;
    api.createGame(d)
      .then(() => this.go(d))
      .catch(e => {
        if (e.unauthorized) this.retryAction = () => this.createGame(d);
        this.setState({ needPasscode: !!e.unauthorized, loadError: e.unauthorized ? null : e.message });
      });
  }

  // Whatever was refused for want of a passcode runs again once it is given.
  submitPasscode() {
    setPasscode(this.state.passcodeInput.trim());
    const again = this.retryAction;
    this.retryAction = null;
    this.setState({ needPasscode: false }, () => (again ? again() : this.save(true)));
  }

  componentDidMount() {
    this.load();
    this.timer = setInterval(() => this.tick(), 1000);
    this.sync = setInterval(() => this.poll(), 10000);
    this.onVisibility = () => {
      if (document.visibilityState === "hidden") this.flush();
      else this.poll();
    };
    document.addEventListener("visibilitychange", this.onVisibility);
    this.onResize = () => {
      this.setState({ isPortrait: window.innerWidth < 900 || window.innerHeight > window.innerWidth * 1.05 });
      this.measureField();
    };
    this.onMove = e => this.handleMove(e);
    this.onUp = e => this.handleUp(e);
    this.onCancel = () => this.cancelDrag();
    // List rows allow vertical scrolling (touch-action: pan-y). Once a player
    // is lifted or already moving, block the scroll so the browser does not
    // take the touch and cancel the drag.
    this.onTouchMove = e => {
      const s = this.state;
      if (s.dragId != null && (s.lifted || s.moved) && e.cancelable) e.preventDefault();
    };
    window.addEventListener("resize", this.onResize);
    window.addEventListener("touchmove", this.onTouchMove, { passive: false });
    window.addEventListener("pointermove", this.onMove, { passive: false });
    window.addEventListener("pointerup", this.onUp);
    window.addEventListener("pointercancel", this.onCancel);
    if (typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => this.measureField());
      if (this.fieldWrap) this.ro.observe(this.fieldWrap);
    }
    this.measureField();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.date !== this.props.date) this.load(this.props.date);
    this.measureField();
  }

  measureField() {
    const el = this.fieldWrap;
    if (!el) return;
    const cs = getComputedStyle(el);
    const w = Math.floor(el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight));
    const h = Math.floor(el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom));
    if (w !== this.state.wrapW || h !== this.state.wrapH) this.setState({ wrapW: w, wrapH: h });
  }

  componentWillUnmount() {
    this.flush();
    clearInterval(this.timer);
    clearTimeout(this.liftTimer);
    clearTimeout(this.saveTimer);
    clearTimeout(this.syncFlag);
    clearInterval(this.sync);
    this.pickStart = null;
    Object.values(this.rosterTimers).forEach(clearTimeout);
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.ro) this.ro.disconnect();
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("touchmove", this.onTouchMove);
    window.removeEventListener("pointermove", this.onMove);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("pointercancel", this.onCancel);
  }

  // Dragging fires a save on every drop, so batch them into one write.
  save(now) {
    if (!this.state.date) return;
    clearTimeout(this.saveTimer);
    if (now) { this.flush(); return; }
    this.saveTimer = setTimeout(() => this.flush(), 1500);
    if (this.state.saveState !== "saving") this.setState({ saveState: "pending" });
  }

  flush() {
    clearTimeout(this.saveTimer);
    const s = this.state;
    if (!s.date || s.loading) return;
    this.setState({ saveState: "saving" });
    api.saveState(s.date, {
      formation: s.formation, period: s.period, elapsed: s.elapsed, running: s.running,
      startedAt: s.startedAt, snap: s.snap, showOpp: s.showOpp, opponents: s.opponents,
      players: s.players.map(p => ({
        id: p.id, num: p.num, name: p.name, pos: p.pos,
        onPitch: p.onPitch, u: p.u, v: p.v, secs: p.secs, seq: p.seq,
      })),
      events: s.log,
    })
      .then(r => {
        if (r && r.updatedAt) this.serverAt = r.updatedAt;
        this.setState({ saveState: "saved" });
      })
      .catch(e => this.writeFailed(e, () => this.flush()));
  }

  writeFailed(e, retry) {
    if (e.unauthorized && retry) this.retryAction = retry;
    this.setState({ saveState: "error", needPasscode: !!e.unauthorized });
  }

  tick() {
    if (!this.state.running) return;
    const cap = this.state.period === 1 ? 2700 : 5400;
    this.setState(s => {
      const elapsed = Math.min(s.elapsed + 1, cap);
      return {
        elapsed,
        running: elapsed < cap,
        players: s.players.map(p => p.onPitch ? { ...p, secs: p.secs + 1 } : p),
      };
    }, () => { if (this.state.elapsed % 10 === 0 || !this.state.running) this.save(); });
  }

  mark() {
    const s = this.state;
    this.history.push({
      geo: s.players.map(p => ({ id: p.id, onPitch: p.onPitch, u: p.u, v: p.v, seq: p.seq })),
      opponents: s.opponents.map(o => ({ ...o })),
      log: s.log.slice(),
      showOpp: s.showOpp,
    });
    if (this.history.length > 40) this.history.shift();
    if (!s.canUndo) this.setState({ canUndo: true });
  }

  undo() {
    const snap = this.history.pop();
    if (!snap) return;
    this.setState(s => ({
      players: s.players.map(p => {
        const g = snap.geo.find(x => x.id === p.id);
        return g ? { ...p, onPitch: g.onPitch, u: g.u, v: g.v, seq: g.seq } : p;
      }),
      opponents: snap.opponents, log: snap.log, showOpp: snap.showOpp,
      selectedId: null, selectedBenchId: null, canUndo: this.history.length > 0,
    }), () => this.save());
  }

  mmss(t) { return String(Math.floor(t / 60)).padStart(2, "0") + ":" + String(t % 60).padStart(2, "0"); }
  initials(n) { return (n || "?").trim().split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase(); }
  nameOf(s, id) { const p = s.players.find(x => x.id === id); return p ? p.num + " " + p.name : ""; }
  samePos(a, b) { return Math.abs(a.u - b.u) < 1e-4 && Math.abs(a.v - b.v) < 1e-4; }
  preGame(s) { return !s.running && s.elapsed === 0; }

  // Before kickoff several players can be listed for one spot. They are stored
  // as on-pitch players with the same u,v; each group is one spot, in the
  // order players were added to it.
  stacks(players) {
    const groups = [];
    players.forEach(p => {
      if (!p.onPitch) return;
      const g = groups.find(x => this.samePos(x[0], p));
      if (g) g.push(p); else groups.push([p]);
    });
    groups.forEach(g => g.sort((a, b) => (a.seq || 0) - (b.seq || 0)));
    return groups;
  }

  nextSeq(s) { return Math.max(0, ...s.players.map(p => p.seq || 0)) + 1; }

  hasStacks(s) { return this.stacks(s.players).some(g => g.length > 1); }

  mates(s, id) {
    const p = s.players.find(x => x.id === id);
    return p && p.onPitch ? s.players.filter(q => q.onPitch && this.samePos(q, p)) : [];
  }

  clampU(u) { return Math.min(0.97, Math.max(0.03, u)); }
  clampV(v) { return Math.min(0.95, Math.max(0.05, v)); }

  rawUV(px, py, rect) {
    if (this.state.isPortrait) return [this.clampU(1 - (py - rect.top) / rect.height), this.clampV((px - rect.left) / rect.width)];
    return [this.clampU((px - rect.left) / rect.width), this.clampV((py - rect.top) / rect.height)];
  }

  snapUV(uv, exceptIds) {
    if (!this.state.snap) return uv;
    const taken = this.state.players.filter(p => p.onPitch && !exceptIds.includes(p.id));
    let best = null, bd = 1e9;
    (SHAPES[this.state.formation] || []).forEach(sl => {
      if (taken.some(p => Math.hypot(p.u - sl[0], p.v - sl[1]) < 0.04)) return;
      const d = Math.hypot(sl[0] - uv[0], sl[1] - uv[1]);
      if (d < bd) { bd = d; best = sl; }
    });
    return best ? [best[0], best[1]] : uv;
  }

  toXY(a, rect) {
    if (this.state.isPortrait) return [rect.left + a[1] * rect.width, rect.top + (1 - a[0]) * rect.height];
    return [rect.left + a[0] * rect.width, rect.top + a[1] * rect.height];
  }

  startDrag(id, from, e, fromList, group) {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    this.tokenDown = true;
    this.fromList = !!fromList;
    this.origin = { x: e.clientX, y: e.clientY };
    clearTimeout(this.liftTimer);
    this.liftTimer = setTimeout(() => {
      if (this.state.dragId === id && !this.state.moved) this.setState({ lifted: true });
    }, 300);
    this.setState({ dragId: id, dragFrom: from, dragGroup: !!group, dragX: e.clientX, dragY: e.clientY, moved: false, lifted: false, hoverId: null, confirmReset: false });
  }

  endDrag() {
    clearTimeout(this.liftTimer);
    this.setState({ dragId: null, dragFrom: null, dragGroup: false, moved: false, lifted: false, hoverId: null });
  }

  cancelDrag() {
    this.tokenDown = false;
    if (this.state.dragId != null) this.endDrag();
  }

  handleMove(e) {
    const s = this.state;
    if (s.dragId == null) return;
    e.preventDefault();
    const dx = Math.abs(e.clientX - this.origin.x), dy = Math.abs(e.clientY - this.origin.y);
    // On a list row, a vertical start is a scroll of the list, not a drag,
    // unless the player was long-pressed first.
    if (!s.moved && !s.lifted && this.fromList && e.pointerType === "touch" && dx + dy > 7 && dy > dx) { this.cancelDrag(); return; }
    const moved = s.moved || dx + dy > 7;
    let hoverId = null;
    if (moved && s.dragFrom !== "opp") {
      const rect = this.field && this.field.getBoundingClientRect();
      if (rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
        const hit = this.hitPlayer(e.clientX, e.clientY, rect, s.dragId);
        if (hit) hoverId = hit.id;
      }
    }
    this.setState({ dragX: e.clientX, dragY: e.clientY, moved, hoverId });
  }

  handleUp(e) {
    const { dragId, dragFrom, dragGroup, moved } = this.state;
    this.tokenDown = false;
    if (dragId == null) return;
    const rect = this.field && this.field.getBoundingClientRect();
    const inside = rect && e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    if (!moved) {
      this.endDrag();
      this.tap(dragId, dragFrom, dragGroup);
      return;
    }
    const target = inside && dragFrom !== "opp" ? this.hitPlayer(e.clientX, e.clientY, rect, dragId) : null;
    const raw = inside ? this.rawUV(e.clientX, e.clientY, rect) : null;
    this.endDrag();
    if (!rect) return;

    if (dragFrom === "opp") {
      if (inside) this.moveOpponent(dragId, raw);
      return;
    }
    if (dragFrom === "bench") {
      if (!inside) return;
      if (target && this.preGame(this.state)) this.stack(dragId, target.id);
      else if (target) this.substitute(dragId, target.id);
      else this.place(dragId, this.snapUV(raw, [dragId]));
    } else {
      const mates = this.mates(this.state, dragId);
      const ids = dragGroup ? mates.map(p => p.id) : [dragId];
      if (!inside) { this.takeOff(ids); return; }
      if (target && !dragGroup && mates.length > 1) this.stack(dragId, target.id);
      else if (target) this.swapSpots(dragId, target.id);
      else if (dragGroup) this.moveGroup(ids, this.snapUV(raw, ids));
      else this.place(dragId, this.snapUV(raw, [dragId]));
    }
  }

  hitPlayer(px, py, rect, exceptId) {
    const size = this.state.isPortrait ? 36 : 44;
    let best = null, bd = size * 1.05;
    const self = this.state.players.find(p => p.id === exceptId);
    this.state.players.forEach(p => {
      if (!p.onPitch || p.id === exceptId) return;
      if (self && self.onPitch && this.samePos(p, self)) return;
      const xy = this.toXY([p.u, p.v], rect);
      const d = Math.hypot(xy[0] - px, xy[1] - py);
      if (d < bd) { bd = d; best = p; }
    });
    return best;
  }

  place(id, uv) {
    if (!uv) return;
    this.mark();
    this.setState(s => {
      const p = s.players.find(x => x.id === id);
      if (!p) return null;
      const others = s.players.filter(q => q.onPitch && q.id !== id);
      const joins = others.some(q => this.samePos(q, { u: uv[0], v: uv[1] }));
      if (!joins && this.stacks(others).length >= CAP) return null;
      return {
        players: s.players.map(q => q.id === id ? { ...q, onPitch: true, u: uv[0], v: uv[1], seq: this.nextSeq(s) } : q),
        log: p.onPitch || this.preGame(s) ? s.log : s.log.concat([{
          at: this.mmss(s.elapsed), atSecs: s.elapsed,
          on: this.nameOf(s, id), onId: id, off: "—", offId: null,
        }]),
        selectedId: null, selectedBenchId: null,
      };
    }, () => this.save());
  }

  moveOpponent(oid, uv) {
    if (!uv) return;
    this.mark();
    this.setState(s => ({
      opponents: s.opponents.map(x => x.oid === oid ? { ...x, u: uv[0], v: uv[1] } : x),
    }), () => this.save());
  }

  // Swaps whole spots, so a stack moves together.
  swapSpots(a, b) {
    this.mark();
    this.setState(s => {
      const pa = s.players.find(p => p.id === a), pb = s.players.find(p => p.id === b);
      if (!pa || !pb) return null;
      return {
        players: s.players.map(p => !p.onPitch ? p
          : this.samePos(p, pa) ? { ...p, u: pb.u, v: pb.v }
          : this.samePos(p, pb) ? { ...p, u: pa.u, v: pa.v } : p),
        selectedId: null, selectedBenchId: null,
      };
    }, () => this.save());
  }

  moveGroup(ids, uv) {
    if (!uv) return;
    this.mark();
    this.setState(s => ({
      players: s.players.map(p => ids.includes(p.id) ? { ...p, u: uv[0], v: uv[1] } : p),
      selectedId: null, selectedBenchId: null,
    }), () => this.save());
  }

  stack(id, targetId) {
    this.mark();
    this.setState(s => {
      const p = s.players.find(x => x.id === id), t = s.players.find(x => x.id === targetId);
      if (!p || !t || !t.onPitch || (p.onPitch && this.samePos(p, t))) return null;
      return {
        players: s.players.map(q => q.id === id ? { ...q, onPitch: true, u: t.u, v: t.v, seq: this.nextSeq(s) } : q),
        log: p.onPitch || this.preGame(s) ? s.log : s.log.concat([{
          at: this.mmss(s.elapsed), atSecs: s.elapsed,
          on: this.nameOf(s, id), onId: id, off: "—", offId: null,
        }]),
        selectedId: null, selectedBenchId: null,
      };
    }, () => this.save());
  }

  substitute(inId, outId) {
    this.mark();
    this.setState(s => {
      const out = s.players.find(p => p.id === outId);
      const inP = s.players.find(p => p.id === inId);
      if (!out || !inP || inP.onPitch || !out.onPitch) return null;
      return {
        players: s.players.map(p => p.id === outId ? { ...p, onPitch: false }
          : p.id === inId ? { ...p, onPitch: true, u: out.u, v: out.v } : p),
        log: s.log.concat([{
          at: this.mmss(s.elapsed), atSecs: s.elapsed,
          on: this.nameOf(s, inId), onId: inId,
          off: this.nameOf(s, outId), offId: outId,
        }]),
        selectedId: null, selectedBenchId: null,
      };
    }, () => this.save());
  }

  takeOff(ids) {
    this.mark();
    this.setState(s => ({
      players: s.players.map(p => ids.includes(p.id) ? { ...p, onPitch: false } : p),
      log: this.preGame(s) ? s.log : s.log.concat(ids.map(id => ({
        at: this.mmss(s.elapsed), atSecs: s.elapsed,
        on: "—", onId: null, off: this.nameOf(s, id), offId: id,
      }))),
      selectedId: null, selectedBenchId: null,
    }), () => this.save());
  }

  tap(id, from, group) {
    const s = this.state;
    if (from === "opp") return;
    const p = s.players.find(x => x.id === id);
    if (!p) return;
    const join = this.preGame(s) ? (a, b) => this.stack(a, b) : (a, b) => this.substitute(a, b);
    if (p.onPitch) {
      if (s.selectedBenchId != null) { join(s.selectedBenchId, id); return; }
      if (group) { this.setState({ selectedId: null }); return; }
      this.setState({ selectedId: s.selectedId === id ? null : id, selectedBenchId: null });
      return;
    }
    if (s.selectedId != null) { join(id, s.selectedId); return; }
    if (this.stacks(s.players).length < CAP) { this.place(id, this.freeSlot()); return; }
    this.setState({ selectedBenchId: s.selectedBenchId === id ? null : id, selectedId: null });
  }

  fieldDown(e) {
    if (this.tokenDown) { this.tokenDown = false; return; }
    const id = this.state.selectedBenchId;
    if (id == null || !this.field) return;
    const rect = this.field.getBoundingClientRect();
    this.place(id, this.snapUV(this.rawUV(e.clientX, e.clientY, rect), [id]));
  }

  syncOpponents(on) {
    this.mark();
    this.setState(s => {
      if (!on) return { showOpp: false, opponents: [] };
      return {
        showOpp: true,
        opponents: Array.from({ length: CAP }, (_, i) => ({
          oid: "o" + Date.now() + i, num: i + 1,
          // v near 1 is the right sideline in portrait, next to the panel. The
          // left sideline touches the screen edge and triggers swipe-back.
          u: (i + 1) / (CAP + 1), v: 0.94,
        })),
      };
    }, () => this.save());
  }

  freeSlot() {
    const s = this.state;
    const taken = s.players.filter(p => p.onPitch);
    if (this.stacks(taken).length >= CAP) return null;
    for (const sl of SHAPES[s.formation]) {
      if (!taken.some(p => Math.hypot(p.u - sl[0], p.v - sl[1]) < 0.06)) return [sl[0], sl[1]];
    }
    return [0.5, 0.5];
  }

  applyFormation(key) {
    this.mark();
    this.setState(s => {
      const slots = SHAPES[key];
      let pool = this.stacks(s.players).concat(s.players.filter(p => !p.onPitch).map(p => [p]));
      const gk = pool.findIndex(g => g.some(p => p.pos === "GK"));
      if (gk > 0) pool = [pool[gk]].concat(pool.filter((_, i) => i !== gk));
      pool = pool.slice(0, slots.length);
      const map = new Map();
      pool.forEach((g, i) => g.forEach(p => map.set(p.id, slots[i])));
      const players = s.players.map(p => map.has(p.id)
        ? { ...p, onPitch: true, u: map.get(p.id)[0], v: map.get(p.id)[1] }
        : { ...p, onPitch: false });
      return { formation: key, players, selectedId: null, selectedBenchId: null };
    }, () => this.save());
  }

  patch(id, patch) {
    this.setState(s => ({
      roster: s.roster.map(p => p.id === id ? { ...p, ...patch } : p),
      players: s.players.map(p => p.id === id ? { ...p, ...patch } : p),
    }));
    clearTimeout(this.rosterTimers[id]);
    this.rosterTimers[id] = setTimeout(() => {
      const p = this.state.roster.find(x => x.id === id);
      if (!p) return;
      api.editPlayer(id, { num: Number(p.num) || 0, name: p.name, pos: p.pos })
        .then(() => this.setState({ saveState: "saved" }))
        .catch(e => this.writeFailed(e, () => this.patch(id, {})));
    }, 800);
  }

  addPlayer() {
    const nums = this.state.roster.map(p => Number(p.num) || 0);
    let n = 1; while (nums.includes(n)) n++;
    api.addPlayer({ num: n, name: "New player", pos: "MID" })
      .then(r => this.setState(s => ({
        roster: s.roster.concat([r.player]).sort(this.byNumber),
        saveState: "saved",
      })))
      .catch(e => this.writeFailed(e, () => this.addPlayer()));
  }

  removePlayer(id) {
    api.removePlayer(id)
      .then(() => this.setState(s => ({
        roster: s.roster.filter(x => x.id !== id),
        players: s.players.filter(x => x.id !== id || x.onPitch || x.secs > 0),
      }), () => this.save(true)))
      .catch(e => this.writeFailed(e, () => this.removePlayer(id)));
  }

  renderVals() {
    const s = this.state;
    const port = s.isPortrait;
    const ink = "var(--color-text)", bone = "var(--color-bg)";
    const surface = "var(--color-surface)", accent = "var(--color-accent)";
    const onPitch = s.players.filter(p => p.onPitch);
    const bench = s.players.filter(p => !p.onPitch);
    const maxSecs = Math.max(60, ...s.players.map(p => p.secs));
    const avg = s.players.length ? s.players.reduce((a, p) => a + p.secs, 0) / s.players.length : 0;
    const warn = true;
    const byNumber = true;

    const stripeA = "#4b8b3d", stripeB = "#417d34";
    const geom = {
      ratio: port ? "68 / 105" : "105 / 68",
      inset: port ? "10px 12px" : "12px 10px",
      grass: "repeating-linear-gradient(" + (port ? "180deg" : "90deg") + "," + stripeA + " 0 50%," + stripeB + " 50% 100%)",
      grassSize: port ? "100% 18%" : "18% 100%",
      token: (port ? 36 : 44) + "px",
      stackToken: (port ? 22 : 28) + "px",
      // A stack's circle sits where a single token's circle would: the single
      // token is centred as circle + 4px gap + name label.
      stackLift: (port ? (36 + 4 + 12) / 2 : (44 + 4 + 13) / 2) + "px",
      stackNameFont: (port ? 11 : 12) + "px",
      stackFont: (port ? 13 : 15) + "px",
      tokenFont: (port ? 15 : 17) + "px",
      oppToken: (port ? 24 : 30) + "px",
      oppFont: (port ? 11 : 13) + "px",
      nameFont: (port ? 9 : 10) + "px",
      nameLine: (port ? 10 : 11) + "px",
      half: port ? { left: "0", top: "50%", w: "100%", h: "2px" } : { left: "50%", top: "0", w: "2px", h: "100%" },
      circle: port ? { w: "30%", h: "auto" } : { w: "auto", h: "30%" },
      boxes: port
        ? [{ left: "20%", top: "0", w: "60%", h: "12%" }, { left: "34%", top: "0", w: "32%", h: "5%" },
           { left: "20%", top: "88%", w: "60%", h: "12%" }, { left: "34%", top: "95%", w: "32%", h: "5%" }]
        : [{ left: "0", top: "20%", w: "12%", h: "60%" }, { left: "0", top: "34%", w: "5%", h: "32%" },
           { left: "88%", top: "20%", w: "12%", h: "60%" }, { left: "95%", top: "34%", w: "5%", h: "32%" }],
    };

    // aspect-ratio alone cannot honour both the width and the height limit at
    // once: whichever axis is not clamped keeps its old value and the pitch
    // stretches out of its box. Size it from the measured wrapper instead.
    // In portrait the pitch fills the whole box and gives up its true ratio,
    // so there is more room to drag players on a small screen.
    const ar = port ? 68 / 105 : 105 / 68;
    let fieldW = s.wrapW, fieldH = port ? s.wrapH : s.wrapW / ar;
    if (!port && fieldH > s.wrapH) { fieldH = s.wrapH; fieldW = s.wrapH * ar; }
    geom.fieldW = fieldW > 0 ? Math.floor(fieldW) + "px" : "100%";
    geom.fieldH = fieldH > 0 ? Math.floor(fieldH) + "px" : "auto";

    const xy = a => ({
      left: (port ? a[1] * 100 : a[0] * 100) + "%",
      top: (port ? (1 - a[0]) * 100 : a[1] * 100) + "%",
    });

    const dragging = s.dragId != null && s.moved;
    const liftId = s.lifted && !s.moved ? s.dragId : null;
    const lift = on => ({ transform: on ? "scale(1.25)" : "scale(1)", transition: "transform 0.12s ease-out" });
    const dragP = dragging && s.dragFrom !== "opp" ? s.players.find(p => p.id === s.dragId) : null;
    const dragIds = s.dragGroup ? this.mates(s, s.dragId).map(p => p.id) : [s.dragId];
    const stacked = this.hasStacks(s);
    const pre = this.preGame(s);
    const dragO = dragging && s.dragFrom === "opp" ? s.opponents.find(o => o.oid === s.dragId) : null;

    const slotMarks = (s.snap && dragging && s.dragFrom !== "opp")
      ? SHAPES[s.formation]
          .filter(sl => !onPitch.some(p => !dragIds.includes(p.id) && Math.hypot(p.u - sl[0], p.v - sl[1]) < 0.04))
          .map(sl => xy(sl))
      : [];

    const stackOf = group => {
      const p = group[0];
      const pos = xy([p.u, p.v]);
      const ids = group.map(q => q.id);
      const gk = group.some(q => q.pos === "GK");
      const target = ids.includes(s.hoverId);
      return {
        id: p.id, stack: true,
        left: pos.left, top: pos.top,
        bg: gk ? bone : ink,
        fg: gk ? ink : bone,
        border: gk ? "2px solid " + ink : "2px solid rgba(255,255,255,0.9)",
        pulse: target ? "subTarget 0.85s ease-out infinite" : "none",
        opacity: s.dragGroup && ids.includes(s.dragId) && s.moved ? 0.3 : 1,
        lift: lift(s.dragGroup && ids.includes(liftId)),
        z: ids.includes(liftId) ? 6 : target || ids.includes(s.selectedId) ? 5 : 3,
        down: e => this.startDrag(p.id, "pitch", e, false, true),
        names: group.map(q => {
          const sel = s.selectedId === q.id;
          return {
            id: q.id,
            label: q.name.split(" ")[0],
            bg: sel ? "#ec3013" : bone,
            fg: sel ? bone : ink,
            opacity: !s.dragGroup && s.dragId === q.id && s.moved ? 0.3 : 1,
            down: e => this.startDrag(q.id, "pitch", e),
          };
        }),
      };
    };

    const tokenOf = group => {
      if (group.length > 1) return stackOf(group);
      const p = group[0];
      const pos = xy([p.u, p.v]);
      const sel = s.selectedId === p.id;
      const target = s.hoverId === p.id;
      const gk = p.pos === "GK";
      return {
        id: p.id,
        left: pos.left, top: pos.top,
        bg: gk ? bone : ink,
        fg: gk ? ink : bone,
        border: gk ? "2px solid " + ink : "2px solid rgba(255,255,255,0.9)",
        badge: byNumber ? String(p.num) : this.initials(p.name),
        label: byNumber ? p.name.split(" ")[0] : "#" + p.num,
        ring: sel ? "0 0 0 3px " + bone + ", 0 0 0 5px #ec3013" : "0 2px 8px rgba(0,0,0,0.35)",
        pulse: target ? "subTarget 0.85s ease-out infinite" : "none",
        opacity: s.dragId === p.id && s.moved ? 0.3 : 1,
        lift: lift(liftId === p.id),
        z: liftId === p.id ? 6 : sel || target ? 5 : 2,
        down: e => this.startDrag(p.id, "pitch", e),
      };
    };

    const rowOf = (p, i) => {
      const low = warn && !p.onPitch && s.elapsed > 240 && p.secs < avg * 0.75;
      const sel = s.selectedId === p.id || s.selectedBenchId === p.id;
      return {
        id: p.id,
        name: p.name, pos: p.pos, time: this.mmss(p.secs),
        avatar: this.initials(p.name),
        timeFg: low ? "var(--color-accent-700)" : ink,
        barW: Math.round((p.secs / maxSecs) * 100) + "%",
        barBg: p.onPitch ? ink : low ? accent : "var(--color-neutral-600)",
        bg: sel ? "var(--color-accent-200)" : (i % 2 ? surface : bone),
        mark: sel ? "inset 4px 0 0 0 #ec3013" : "none",
        avatarBg: p.onPitch ? (p.pos === "GK" ? "var(--color-neutral-300)" : ink) : "var(--color-neutral-300)",
        avatarFg: p.onPitch && p.pos !== "GK" ? bone : "var(--color-neutral-800)",
        posFg: p.pos === "GK" ? "var(--color-accent-700)" : "var(--color-neutral-700)",
        opacity: s.dragId === p.id && s.moved ? 0.35 : 1,
        lift: lift(liftId === p.id),
        z: liftId === p.id ? 1 : "auto",
        cursor: p.onPitch ? "pointer" : "grab",
        down: e => this.startDrag(p.id, p.onPitch ? "pitch" : "bench", e, true),
      };
    };

    let list;
    if (s.tab === "minutes") {
      list = {
        title: "Play time", hint: onPitch.length + " on pitch",
        rows: s.players.slice().sort((a, b) => b.secs - a.secs).map(rowOf),
        subs: [], roster: [], empty: false, emptyText: "", showAdd: false,
        footer: "Squad average " + this.mmss(Math.round(avg)),
      };
    } else if (s.tab === "subs") {
      list = {
        title: "Substitutions", hint: s.log.length + " logged", rows: [], roster: [],
        subs: s.log.slice().reverse().map((e, i) => ({ ...e, bg: i % 2 ? surface : bone })),
        empty: s.log.length === 0, showAdd: false,
        emptyText: "No changes yet. Drag a bench player onto a player on the pitch, or tap one then the other, to swap them.",
        footer: "Unlimited subs · match clock times",
      };
    } else if (s.tab === "roster") {
      list = {
        title: "Roster", hint: s.roster.length + " players", rows: [], subs: [], empty: false, emptyText: "",
        roster: s.roster.map((p, i) => ({
          id: p.id,
          name: p.name, num: String(p.num), pos: p.pos,
          bg: i % 2 ? surface : bone,
          avatar: this.initials(p.name),
          avatarBg: "var(--color-neutral-300)",
          avatarFg: "var(--color-neutral-800)",
          setName: e => this.patch(p.id, { name: e.target.value }),
          setNum: e => this.patch(p.id, { num: e.target.value.replace(/\D/g, "").slice(0, 2) }),
          cyclePos: () => this.patch(p.id, { pos: POSITIONS[(POSITIONS.indexOf(p.pos) + 1) % 4] }),
          remove: () => this.removePlayer(p.id),
        })),
        showAdd: true, footer: "Roster changes are shared with every captain",
      };
    } else {
      list = {
        title: "Bench", hint: bench.length + " available", rows: bench.map(rowOf), subs: [], roster: [],
        empty: bench.length === 0, showAdd: false, showCallUp: true,
        emptyText: s.players.length === 0
          ? "Nobody is in this squad yet. Call up the players who showed up."
          : "Everyone in the squad is on the pitch.",
        footer: s.selectedBenchId != null ? (pre ? "Now tap a player on the pitch to stack" : "Now tap a player on the pitch to swap")
          : s.selectedId != null ? (pre ? "Now tap a bench player to stack them" : "Now tap a bench player to sub them in")
          : "",
      };
    }

    // Picking who is available for this game, over whichever tab was open.
    if (s.picking) {
      const inSquad = new Map(s.players.map(p => [p.id, p]));
      const q = s.query.trim().toLowerCase();
      const matches = q ? s.roster.filter(p => (p.name || "").toLowerCase().includes(q)) : s.roster;
      list = {
        title: "Call up", hint: s.players.length + " in squad",
        rows: [], subs: [], roster: [], empty: matches.length === 0, showAdd: false,
        emptyText: s.roster.length === 0
          ? "The roster is empty. Add players on the Roster tab first."
          : "No name matches that.",
        showDone: true,
        footer: "",
        pick: matches.map((p, i) => {
          const sq = inSquad.get(p.id);
          const locked = !!sq && (sq.onPitch || sq.secs > 0);
          return {
            id: p.id, name: p.name, pos: p.pos, num: String(p.num),
            avatar: this.initials(p.name),
            avatarBg: sq ? ink : "var(--color-neutral-300)",
            avatarFg: sq ? bone : "var(--color-neutral-800)",
            bg: i % 2 ? surface : bone,
            mark: locked ? this.mmss(sq.secs) : sq ? "✓" : "+",
            markFg: locked ? "var(--color-neutral-700)" : sq ? ink : "var(--color-neutral-500)",
            posFg: p.pos === "GK" ? "var(--color-accent-700)" : "var(--color-neutral-700)",
            cursor: locked ? "default" : "pointer",
            act: () => { if (!locked) this.toggleSquad(p.id); },
          };
        }),
      };
    }
    list.pick = list.pick || [];

    const tab = (key, label) => ({
      key, label, bg: s.tab === key && !s.picking ? ink : bone, fg: s.tab === key && !s.picking ? bone : ink,
      pick: () => this.setState({ tab: key, picking: false, query: "" }),
    });

    return {
      ui: {
        port,
        panelW: port ? "26%" : "300px",
        panelFlex: port ? "0 0 26%" : "0 0 300px",
        panelMaxW: port ? 130 : "100%",
        clockSize: port ? "22px" : "46px",
        headPad: port ? "6px 12px" : "12px 16px",
        headPad2: port ? "6px 12px" : "8px 16px",
        clockGap: port ? 10 : 16,
        startBtnPad: port ? "7px 10px" : "10px 16px",
        startBtnMinW: port ? 0 : 160,
        startBtnFont: port ? 11 : 13,
        toolPad: port ? "5px 8px" : "8px 12px",
        btnPad: port ? "5px 7px" : "6px 10px",
        btnFont: port ? 11 : 12,
        btnTrack: port ? "0.04em" : "0.1em",
        actBtnFlex: port ? "1 1 0" : "0 0 auto",
        fieldPad: port ? 0 : 12,
      },
      geom, slotMarks,
      pitchTokens: this.stacks(onPitch).map(tokenOf),
      oppTokens: s.opponents.map(o => {
        const pos = xy([o.u, o.v]);
        return {
          oid: o.oid, left: pos.left, top: pos.top, num: o.num,
          opacity: s.dragId === o.oid && s.moved ? 0.3 : 1,
          lift: lift(liftId === o.oid),
          down: e => this.startDrag(o.oid, "opp", e),
        };
      }),
      clock: {
        elapsed: this.mmss(s.elapsed),
        tint: s.running ? ink : "var(--color-neutral-600)",
        kickoff: s.startedAt ? new Date(s.startedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—",
        periodLabel: "Half " + s.period,
        btnLabel: s.running ? "Half " + s.period + " running" : s.elapsed === 0 ? (stacked ? "Pick 1 per spot" : "Start first half") : s.elapsed >= 2700 && s.elapsed < 5400 ? "Start second half" : "Full time",
        btnDisabled: s.running || s.elapsed >= 5400 || (pre && stacked),
        btnBg: s.running || s.elapsed >= 5400 || (pre && stacked) ? "transparent" : accent,
        btnFg: s.running || s.elapsed >= 5400 || (pre && stacked) ? ink : bone,
        toggle: () => this.setState(st => {
          if (st.running || st.elapsed >= 5400) return null;
          if (st.elapsed === 0) return this.hasStacks(st) ? null : { running: true, startedAt: Date.now(), period: 1 };
          if (st.elapsed >= 2700) return { running: true, period: 2 };
          return null;
        }, () => this.save()),
      },
      tabs: [tab("bench", "Bench"), tab("minutes", port ? "Mins" : "Minutes"), tab("subs", "Subs"), tab("roster", "Roster")],
      formations: Object.keys(SHAPES).map(k => ({
        key: k, label: k, bg: s.formation === k ? ink : "transparent", fg: s.formation === k ? bone : ink,
        pick: () => { this.setState({ formationOpen: false }); this.applyFormation(k); },
      })),
      formation: s.formation,
      formationOpen: s.formationOpen,
      toggleFormations: () => this.setState(st => ({ formationOpen: !st.formationOpen })),
      closeFormations: () => this.setState({ formationOpen: false }),
      game: {
        label: dateLabel(s.date),
        short: s.date ? s.date.slice(5).replace("-", "/") : "—",
        open: s.gamesOpen,
        failed: s.saveState === "error",
        toggle: () => this.setState(st => ({ gamesOpen: !st.gamesOpen })),
        close: () => this.setState({ gamesOpen: false }),
        items: s.games.map(g => ({
          id: g.id, label: dateLabel(g.id),
          bg: g.id === s.date ? ink : "transparent",
          fg: g.id === s.date ? bone : ink,
          pick: () => this.go(g.id),
        })),
        newDate: s.newDate,
        setNewDate: e => this.setState({ newDate: e.target.value }),
        create: () => this.createGame(),
        saveLabel: s.justSynced && s.saveState !== "error"
          ? "Updated from another device"
          : { pending: "Saving…", saving: "Saving…", saved: "Saved", error: "Not saved" }[s.saveState] || "",
        saveFg: s.saveState === "error" ? "var(--color-accent-700)" : "var(--color-neutral-700)",
      },
      list,
      startPick: () => this.setState({ picking: true, query: "" }),
      endPick: () => this.setState({ picking: false, query: "" }),
      filter: {
        show: s.picking,
        value: s.query,
        set: e => this.setState({ query: e.target.value }),
      },
      actions: {
        undo: () => this.undo(),
        undoFg: s.canUndo ? ink : "var(--color-neutral-500)",
        undoBorder: s.canUndo ? ink : "var(--color-neutral-400)",
        undoCursor: s.canUndo ? "pointer" : "default",
        snapLabel: port ? (s.snap ? "Snap ✓" : "Snap") : (s.snap ? "Snap: on" : "Snap: off"),
        snapBg: s.snap ? ink : "transparent",
        snapFg: s.snap ? bone : ink,
        toggleSnap: () => {
          const next = !s.snap;
          this.setState({ snap: next }, () => {
            if (!next && !this.state.showOpp) this.syncOpponents(true); else this.save();
          });
        },
        oppLabel: port ? (s.showOpp ? "Opp ✓" : "Opp") : (s.showOpp ? "Opponents: on" : "Opponents: off"),
        oppBg: s.showOpp ? accent : "transparent",
        oppFg: s.showOpp ? bone : ink,
        oppBorder: s.showOpp ? accent : ink,
        toggleOpp: () => this.syncOpponents(!s.showOpp),
        clear: () => {
          this.mark();
          this.setState(st => ({ players: st.players.map(p => ({ ...p, onPitch: false })), opponents: [], selectedId: null, selectedBenchId: null }), () => this.save());
        },
        fieldDown: e => this.fieldDown(e),
        addPlayer: () => this.addPlayer(),
        resetLabel: s.confirmReset ? (port ? "Sure?" : "Tap to confirm") : "Reset",
        resetBg: s.confirmReset ? accent : "transparent",
        resetFg: s.confirmReset ? bone : ink,
        reset: () => {
          if (!s.confirmReset) { this.setState({ confirmReset: true }); return; }
          this.history = [];
          this.setState(st => ({
            ...this.freshState(),
            tab: st.tab,
            players: st.players.map(p => ({ ...p, onPitch: false, u: 0.5, v: 0.5, secs: 0 })),
          }), () => this.save(true));
        },
      },
      ghost: {
        show: !!dragP,
        x: s.dragX + "px", y: s.dragY + "px",
        bg: dragP && dragP.pos === "GK" ? bone : ink,
        fg: dragP && dragP.pos === "GK" ? ink : bone,
        badge: !dragP ? "" : s.dragGroup ? "-" : byNumber ? String(dragP.num) : this.initials(dragP.name),
        label: !dragP ? "" : s.dragGroup ? this.mates(s, dragP.id).map(q => q.name.split(" ")[0]).join(" / ") : dragP.name,
      },
      oppGhost: {
        show: !!dragO,
        x: s.dragX + "px", y: s.dragY + "px",
        num: dragO ? dragO.num : "",
      },
    };
  }

  renderPasscode() {
    if (!this.state.needPasscode) return null;
    const ink = "var(--color-text)", bone = "var(--color-bg)";
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "grid", placeItems: "center", background: "rgba(45,43,43,0.5)", padding: 16 }}>
        <div style={{ width: "100%", maxWidth: 340, background: bone, border: "2px solid " + ink, padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Passcode</div>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--color-neutral-700)" }}>
            Anyone can look at the board. Changing it needs the passcode the captains share.
          </div>
          <input
            type="password" autoFocus value={this.state.passcodeInput}
            onChange={e => this.setState({ passcodeInput: e.target.value })}
            onKeyDown={e => { if (e.key === "Enter") this.submitPasscode(); }}
            style={{ border: "2px solid " + ink, padding: "8px 10px", fontSize: 16, fontWeight: 600 }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <button onPointerDown={() => this.submitPasscode()} style={{ flex: "1 1 auto", background: ink, color: bone, padding: "9px 12px", fontWeight: 700, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>Unlock</button>
            <button onPointerDown={() => this.setState({ needPasscode: false })} style={{ flex: "0 0 auto", background: "transparent", border: "2px solid " + ink, padding: "9px 12px", fontWeight: 700, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  renderGate() {
    const s = this.state;
    const ink = "var(--color-text)", bone = "var(--color-bg)";
    const btn = { background: ink, color: bone, padding: "9px 14px", fontWeight: 700, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase" };
    const note = { fontSize: 13, lineHeight: 1.6, color: "var(--color-neutral-700)" };
    let body;
    if (s.loading) {
      body = <div style={note}>Loading…</div>;
    } else if (s.loadError) {
      body = (
        <>
          <div style={{ fontWeight: 800, fontSize: 18 }}>Could not load the board</div>
          <div style={note}>{s.loadError}</div>
          <button onPointerDown={() => this.load()} style={btn}>Try again</button>
        </>
      );
    } else {
      body = (
        <>
          <div style={{ fontWeight: 800, fontSize: 18 }}>No upcoming game</div>
          <div style={note}>Pick a date to start one. Everyone with the passcode edits the same lineup.</div>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type="date" value={s.newDate}
              onChange={e => this.setState({ newDate: e.target.value })}
              style={{ flex: "1 1 auto", minWidth: 0, border: "2px solid " + ink, padding: "8px 10px", fontSize: 16, fontWeight: 600 }}
            />
            <button onPointerDown={() => this.createGame()} style={btn}>Create</button>
          </div>
          {s.games.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, borderTop: "2px solid " + ink, paddingTop: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--color-neutral-700)" }}>Past games</div>
              {s.games.map(g => (
                <button key={g.id} onPointerDown={() => this.go(g.id)} style={{ textAlign: "left", background: "transparent", padding: "7px 0", fontWeight: 700, fontSize: 13 }}>{dateLabel(g.id)}</button>
              ))}
            </div>
          )}
        </>
      );
    }
    return (
      <div className="soccer-board" style={{ display: "block", overflowY: "auto" }}>
        <div style={{ maxWidth: 420, margin: "0 auto", padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>{body}</div>
        {this.renderPasscode()}
      </div>
    );
  }

  render() {
    if (this.state.loading || this.state.loadError || !this.state.date) return this.renderGate();
    const v = this.renderVals();
    const port = v.ui.port;
    const ink = "var(--color-text)", bone = "var(--color-bg)";
    const smallLabel = { fontSize: 10, fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--color-neutral-700)" };

    const elapsed = (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={smallLabel}>Elapsed</div>
        <div style={{ fontWeight: 800, fontSize: v.ui.clockSize, lineHeight: 1, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em", color: v.clock.tint }}>{v.clock.elapsed}</div>
      </div>
    );
    const startBtn = (
      <button
        onPointerDown={v.clock.toggle}
        disabled={v.clock.btnDisabled}
        style={{ background: v.clock.btnBg, color: v.clock.btnFg, padding: v.ui.startBtnPad, minWidth: v.ui.startBtnMinW, textAlign: "left", fontWeight: 700, fontSize: v.ui.startBtnFont, letterSpacing: "0.08em", textTransform: "uppercase", border: "2px solid " + ink }}
      >{v.clock.btnLabel}</button>
    );

    const gamePicker = (
      <div style={{ position: "relative", flex: "0 0 auto" }}>
        <button
          onPointerDown={v.game.toggle}
          style={{ whiteSpace: "nowrap", background: v.game.failed ? "var(--color-accent)" : "transparent", color: v.game.failed ? bone : ink, border: "2px solid " + (v.game.failed ? "var(--color-accent)" : ink), padding: port ? "4px 6px" : "6px 10px", fontWeight: 800, fontSize: port ? 12 : 15, letterSpacing: "0.02em", fontVariantNumeric: "tabular-nums" }}
        >{port ? v.game.short : v.game.label} ▾</button>
        {v.game.open && (
          <>
            <div onPointerDown={v.game.close} style={{ position: "fixed", inset: 0, zIndex: 30 }}></div>
            <div style={{ position: "absolute", left: 0, top: "calc(100% + 4px)", zIndex: 31, minWidth: 250, maxHeight: 320, overflowY: "auto", background: bone, border: "2px solid " + ink, boxShadow: "0 8px 20px rgba(0,0,0,0.25)" }}>
              {v.game.items.map(g => (
                <button key={g.id} onPointerDown={g.pick} style={{ display: "block", width: "100%", textAlign: "left", background: g.bg, color: g.fg, padding: "9px 12px", fontWeight: 700, fontSize: 12.5, whiteSpace: "nowrap" }}>{g.label}</button>
              ))}
              <div style={{ display: "flex", gap: 4, padding: 8, borderTop: "2px solid " + ink }}>
                <input type="date" value={v.game.newDate} onChange={v.game.setNewDate} style={{ flex: "1 1 auto", minWidth: 0, border: "2px solid " + ink, padding: "4px 6px", fontSize: 16, fontWeight: 600 }} />
                <button onPointerDown={v.game.create} style={{ background: ink, color: bone, padding: "4px 10px", fontWeight: 700, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>Add</button>
              </div>
            </div>
          </>
        )}
      </div>
    );

    const toolbar = (
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: v.ui.toolPad, borderBottom: "2px solid " + ink, flexWrap: port ? "nowrap" : "wrap" }}>
        {port ? (
          <div style={{ position: "relative", flex: "0 0 auto" }}>
            <button onPointerDown={v.toggleFormations} style={{ whiteSpace: "nowrap", background: ink, color: bone, border: "2px solid " + ink, padding: v.ui.btnPad, fontWeight: 700, fontSize: v.ui.btnFont, letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums" }}>{v.formation} ▾</button>
            {v.formationOpen && (
              <>
                <div onPointerDown={v.closeFormations} style={{ position: "fixed", inset: 0, zIndex: 20 }}></div>
                <div style={{ position: "absolute", left: 0, top: "calc(100% + 4px)", zIndex: 21, display: "flex", flexDirection: "column", minWidth: "100%", background: bone, border: "2px solid " + ink, boxShadow: "0 8px 20px rgba(0,0,0,0.25)" }}>
                  {v.formations.map(f => (
                    <button key={f.key} onPointerDown={f.pick} style={{ whiteSpace: "nowrap", background: f.bg, color: f.fg, padding: "10px 12px", textAlign: "left", fontWeight: 700, fontSize: 13, letterSpacing: "0.04em", fontVariantNumeric: "tabular-nums" }}>{f.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : v.formations.map(f => (
          <button key={f.key} onPointerDown={f.pick} style={{ background: f.bg, color: f.fg, border: "2px solid " + ink, padding: v.ui.btnPad, fontWeight: 700, fontSize: v.ui.btnFont, letterSpacing: "0.06em", fontVariantNumeric: "tabular-nums" }}>{f.label}</button>
        ))}
        {!port && <div style={{ flex: "1 1 auto" }}></div>}
        <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", flex: port ? "1 1 auto" : "0 0 auto", minWidth: 0 }}>
          <button onPointerDown={v.actions.undo} style={{ flex: v.ui.actBtnFlex, whiteSpace: "nowrap", background: "transparent", color: v.actions.undoFg, border: "2px solid " + v.actions.undoBorder, padding: v.ui.btnPad, fontWeight: 700, fontSize: v.ui.btnFont, letterSpacing: v.ui.btnTrack, textTransform: "uppercase", cursor: v.actions.undoCursor }}>{v.ui.port ? "↶" : "↶ Undo"}</button>
          <button onPointerDown={v.actions.toggleSnap} style={{ flex: v.ui.actBtnFlex, whiteSpace: "nowrap", background: v.actions.snapBg, color: v.actions.snapFg, border: "2px solid " + ink, padding: v.ui.btnPad, fontWeight: 700, fontSize: v.ui.btnFont, letterSpacing: v.ui.btnTrack, textTransform: "uppercase" }}>{v.actions.snapLabel}</button>
          <button onPointerDown={v.actions.toggleOpp} style={{ flex: v.ui.actBtnFlex, whiteSpace: "nowrap", background: v.actions.oppBg, color: v.actions.oppFg, border: "2px solid " + v.actions.oppBorder, padding: v.ui.btnPad, fontWeight: 700, fontSize: v.ui.btnFont, letterSpacing: v.ui.btnTrack, textTransform: "uppercase" }}>{v.actions.oppLabel}</button>
          <button onPointerDown={v.actions.clear} style={{ flex: v.ui.actBtnFlex, whiteSpace: "nowrap", background: "transparent", border: "2px solid " + ink, padding: v.ui.btnPad, fontWeight: 700, fontSize: v.ui.btnFont, letterSpacing: v.ui.btnTrack, textTransform: "uppercase" }}>Clear</button>
          <button onPointerDown={v.actions.reset} style={{ flex: v.ui.actBtnFlex, whiteSpace: "nowrap", background: v.actions.resetBg, color: v.actions.resetFg, border: "2px solid " + ink, padding: v.ui.btnPad, fontWeight: 700, fontSize: v.ui.btnFont, letterSpacing: v.ui.btnTrack, textTransform: "uppercase" }}>{v.actions.resetLabel}</button>
        </div>
      </div>
    );

    return (
      <div className="soccer-board">
        {v.ui.port ? (
          <div style={{ display: "flex", alignItems: "center", gap: v.ui.clockGap, padding: v.ui.headPad, borderBottom: "2px solid " + ink }}>
            {gamePicker}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={smallLabel}>Kickoff</div>
              <div style={{ fontWeight: 800, fontSize: v.ui.clockSize, lineHeight: 1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{v.clock.kickoff}</div>
            </div>
            {elapsed}
            <div style={{ flex: "1 1 auto" }}></div>
            {startBtn}
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "stretch", borderBottom: "2px solid " + ink, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 200px", minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 2, padding: v.ui.headPad }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {gamePicker}
                {v.game.saveLabel && <div style={{ ...smallLabel, color: v.game.saveFg, whiteSpace: "nowrap" }}>{v.game.saveLabel}</div>}
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-neutral-700)" }}>
                11 v 11 · {v.clock.periodLabel} · Kickoff {v.clock.kickoff}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: v.ui.clockGap, padding: v.ui.headPad2 }}>
              {elapsed}
              {startBtn}
            </div>
          </div>
        )}

        {v.ui.port && toolbar}

        <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden", display: "flex" }}>

          <div style={{ flex: "1 1 auto", minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {!v.ui.port && toolbar}

            <div ref={el => { this.fieldWrap = el; }} style={{ flex: "1 1 auto", minHeight: 0, display: "grid", placeItems: "center", overflow: "hidden", padding: v.ui.fieldPad }}>
              <div
                ref={el => { this.field = el; }}
                onPointerDown={v.actions.fieldDown}
                style={{ position: "relative", width: v.geom.fieldW, height: v.geom.fieldH, aspectRatio: v.geom.ratio, backgroundImage: v.geom.grass, backgroundSize: v.geom.grassSize, touchAction: "none" }}
              >
                <div style={{ position: "absolute", inset: v.geom.inset, border: "2px solid rgba(255,255,255,0.7)" }}>
                  <div style={{ position: "absolute", left: v.geom.half.left, top: v.geom.half.top, width: v.geom.half.w, height: v.geom.half.h, background: "rgba(255,255,255,0.7)" }}></div>
                  <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: v.geom.circle.w, height: v.geom.circle.h, aspectRatio: 1, border: "2px solid rgba(255,255,255,0.7)", borderRadius: "50%" }}></div>
                  <div style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%,-50%)", width: 7, height: 7, borderRadius: "50%", background: "rgba(255,255,255,0.8)" }}></div>
                  {v.geom.boxes.map((b, i) => (
                    <div key={i} style={{ position: "absolute", left: b.left, top: b.top, width: b.w, height: b.h, border: "2px solid rgba(255,255,255,0.7)" }}></div>
                  ))}
                </div>

                {v.slotMarks.map((m, i) => (
                  <div key={i} style={{ position: "absolute", left: m.left, top: m.top, transform: "translate(-50%,-50%)", width: 14, height: 14, borderRadius: "50%", border: "2px dashed rgba(255,255,255,0.85)" }}></div>
                ))}

                {v.oppTokens.map(o => (
                  <div key={o.oid} onPointerDown={o.down} style={{ position: "absolute", left: o.left, top: o.top, transform: "translate(-50%,-50%) " + o.lift.transform, transition: o.lift.transition, width: v.geom.oppToken, height: v.geom.oppToken, borderRadius: "50%", display: "grid", placeItems: "center", background: "var(--color-accent)", color: bone, fontWeight: 700, fontSize: v.geom.oppFont, fontVariantNumeric: "tabular-nums", boxShadow: "0 2px 6px rgba(0,0,0,0.3)", touchAction: "none", cursor: "grab", opacity: o.opacity, zIndex: 1 }}>{o.num}</div>
                ))}

                {v.pitchTokens.map(t => t.stack ? (
                  <div key={t.id} style={{ position: "absolute", left: t.left, top: t.top, transform: "translate(-50%, -" + v.geom.stackLift + ")", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, zIndex: t.z }}>
                    <div onPointerDown={t.down} style={{ width: v.geom.stackToken, height: v.geom.stackToken, borderRadius: "50%", display: "grid", placeItems: "center", background: t.bg, color: t.fg, fontWeight: 800, fontSize: v.geom.stackFont, lineHeight: 1, border: t.border, boxShadow: "0 2px 8px rgba(0,0,0,0.35)", animation: t.pulse, touchAction: "none", cursor: "grab", opacity: t.opacity, ...t.lift }}>-</div>
                    {t.names.map(n => (
                      <div key={n.id} onPointerDown={n.down} style={{ padding: "1px 5px", background: n.bg, color: n.fg, fontSize: v.geom.stackNameFont, fontWeight: 700, letterSpacing: "0.04em", whiteSpace: "nowrap", maxWidth: 96, overflow: "hidden", textOverflow: "ellipsis", touchAction: "none", cursor: "grab", opacity: n.opacity }}>{n.label}</div>
                    ))}
                  </div>
                ) : (
                  <div key={t.id} onPointerDown={t.down} style={{ position: "absolute", left: t.left, top: t.top, transform: "translate(-50%,-50%)", display: "flex", flexDirection: "column", alignItems: "center", touchAction: "none", cursor: "grab", opacity: t.opacity, zIndex: t.z }}>
                    <div style={{ width: v.geom.token, height: v.geom.token, borderRadius: "50%", display: "grid", placeItems: "center", overflow: "hidden", background: t.bg, color: t.fg, fontWeight: 800, fontSize: v.geom.tokenFont, fontVariantNumeric: "tabular-nums", border: t.border, boxShadow: t.ring, animation: t.pulse, ...t.lift }}>{t.badge}</div>
                    <div style={{ marginTop: 4, padding: "1px 5px", background: bone, color: ink, fontSize: v.geom.nameFont, lineHeight: v.geom.nameLine, fontWeight: 700, letterSpacing: "0.04em", whiteSpace: "nowrap", maxWidth: 96, overflow: "hidden", textOverflow: "ellipsis" }}>{t.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ flex: v.ui.panelFlex, width: v.ui.panelW, maxWidth: v.ui.panelMaxW, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", borderLeft: "2px solid " + ink }}>

            {port ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 2, background: ink, borderBottom: "2px solid " + ink }}>
                {v.tabs.map(t => (
                  <button key={t.key} onPointerDown={t.pick} style={{ minWidth: 0, background: t.bg, color: t.fg, padding: "6px 8px", textAlign: "left", fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>{t.label}</button>
                ))}
              </div>
            ) : (
              <div style={{ display: "flex", borderBottom: "2px solid " + ink }}>
                {v.tabs.map(t => (
                  <button key={t.key} onPointerDown={t.pick} style={{ flex: "1 1 0", minWidth: 0, background: t.bg, color: t.fg, padding: "10px 8px", textAlign: "left", fontWeight: 700, fontSize: 11.5, letterSpacing: "0.08em", textTransform: "uppercase" }}>{t.label}</button>
                ))}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: port ? "6px 8px" : "12px 14px 8px" }}>
              {!port && <div style={{ fontWeight: 800, fontSize: 14 }}>{v.list.title}</div>}
              <div style={{ fontSize: port ? 10 : 11, fontWeight: 600, letterSpacing: port ? "0.04em" : "0.1em", whiteSpace: "nowrap", textTransform: "uppercase", color: "var(--color-neutral-700)" }}>{v.list.hint}</div>
            </div>

            {v.filter.show && (
              <div style={{ padding: port ? "0 6px 6px" : "0 14px 8px" }}>
                <input
                  ref={el => { this.filterInput = el; }}
                  value={v.filter.value} onChange={v.filter.set} autoFocus={!port}
                  placeholder={port ? "Name" : "Filter by name"}
                  style={{ width: "100%", boxSizing: "border-box", background: bone, color: ink, border: "2px solid " + ink, padding: port ? "4px 5px" : "7px 9px", fontSize: 16, fontWeight: 600 }}
                />
              </div>
            )}

            <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", overflowX: "hidden", overscrollBehavior: "contain" }}>

              {v.list.rows.map(r => port ? (
                <div key={r.id} onPointerDown={r.down} style={{ position: "relative", zIndex: r.z, display: "flex", alignItems: "center", gap: 5, background: r.bg, padding: "5px 6px", touchAction: "pan-y", cursor: r.cursor, opacity: r.opacity, boxShadow: r.mark }}>
                  <div style={{ flex: "0 0 auto", width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center", overflow: "hidden", background: r.avatarBg, color: r.avatarFg, fontWeight: 700, fontSize: 9, ...r.lift }}>{r.avatar}</div>
                  <div style={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", color: r.posFg }}>{r.pos}</span>
                      <span style={{ flex: "1 1 auto" }}></span>
                      <span style={{ fontWeight: 700, fontSize: 10.5, fontVariantNumeric: "tabular-nums", color: r.timeFg }}>{r.time}</span>
                    </div>
                    <span style={{ height: 3, background: "var(--color-neutral-300)" }}><span style={{ display: "block", height: 3, width: r.barW, background: r.barBg }}></span></span>
                  </div>
                </div>
              ) : (
                <div key={r.id} onPointerDown={r.down} style={{ position: "relative", zIndex: r.z, display: "flex", alignItems: "center", gap: 10, background: r.bg, padding: "8px 14px", touchAction: "pan-y", cursor: r.cursor, opacity: r.opacity, boxShadow: r.mark }}>
                  <div style={{ flex: "0 0 auto", width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center", overflow: "hidden", background: r.avatarBg, color: r.avatarFg, fontWeight: 700, fontSize: 13, ...r.lift }}>{r.avatar}</div>
                  <div style={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", color: r.posFg }}>{r.pos}</span>
                      <span style={{ flex: "1 1 auto", height: 3, background: "var(--color-neutral-300)" }}><span style={{ display: "block", height: 3, width: r.barW, background: r.barBg }}></span></span>
                    </div>
                  </div>
                  <div style={{ flex: "0 0 auto", fontWeight: 700, fontSize: 14, fontVariantNumeric: "tabular-nums", color: r.timeFg }}>{r.time}</div>
                </div>
              ))}

              {v.list.subs.map((s, i) => (
                <div key={i} style={{ display: "flex", flexDirection: port ? "column" : "row", alignItems: port ? "stretch" : "center", gap: port ? 1 : 12, padding: port ? "6px 8px" : "8px 14px", background: s.bg }}>
                  <div style={{ minWidth: 44, fontWeight: 700, fontSize: port ? 12 : 14, fontVariantNumeric: "tabular-nums" }}>{s.at}</div>
                  <div style={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                    <div style={{ fontSize: port ? 11.5 : 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><span style={{ fontWeight: 700, letterSpacing: "0.08em", color: "var(--color-accent-700)" }}>IN</span> {s.on}</div>
                    <div style={{ fontSize: port ? 11.5 : 12.5, color: "var(--color-neutral-700)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}><span style={{ fontWeight: 700, letterSpacing: "0.08em" }}>OUT</span> {s.off}</div>
                  </div>
                </div>
              ))}

              {v.list.roster.map(p => {
                const avatar = <div style={{ flex: "0 0 auto", width: port ? 26 : 38, height: port ? 26 : 38, borderRadius: "50%", display: "grid", placeItems: "center", background: p.avatarBg, color: p.avatarFg, fontWeight: 700, fontSize: port ? 10 : 12 }}>{p.avatar}</div>;
                const numIn = <input value={p.num} onChange={p.setNum} inputMode="numeric" style={{ flex: port ? "0 0 26px" : "0 0 38px", width: port ? 26 : 38, background: "transparent", border: "none", borderBottom: "2px solid var(--color-neutral-400)", padding: "4px 2px", fontWeight: 700, fontSize: 16, textAlign: "center", fontVariantNumeric: "tabular-nums" }} />;
                const nameIn = <input value={p.name} onChange={p.setName} style={{ flex: "1 1 auto", minWidth: 0, background: "transparent", border: "none", borderBottom: "2px solid var(--color-neutral-400)", padding: "4px 4px", fontSize: 16, fontWeight: 600 }} />;
                const posBtn = <button onPointerDown={p.cyclePos} style={{ flex: "0 0 auto", minWidth: port ? 0 : 48, textAlign: "left", background: "transparent", border: "2px solid " + ink, padding: port ? "2px 3px" : "4px 7px", fontWeight: 700, fontSize: port ? 10 : 11, letterSpacing: port ? "0.04em" : "0.08em" }}>{p.pos}</button>;
                const removeBtn = <button onPointerDown={p.remove} title="Remove player" style={{ flex: "0 0 auto", width: 26, height: 26, background: "transparent", color: "var(--color-neutral-700)", fontWeight: 700, fontSize: 15, lineHeight: 1 }}>×</button>;
                return port ? (
                  <div key={p.id} style={{ display: "flex", flexDirection: "column", gap: 4, padding: "5px 6px", background: p.bg }}>
                    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>{numIn}<span style={{ flex: "1 1 auto" }}></span>{posBtn}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>{nameIn}{removeBtn}</div>
                  </div>
                ) : (
                  <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", background: p.bg }}>
                    {avatar}{numIn}{nameIn}{posBtn}{removeBtn}
                  </div>
                );
              })}

              {v.list.pick.map(p => (
                <div
                  key={p.id}
                  onPointerDown={e => this.pickDown(e, p)}
                  onPointerUp={e => this.pickUp(e, p)}
                  onPointerCancel={() => { this.pickStart = null; }}
                  style={{ display: "flex", alignItems: "center", gap: port ? 5 : 10, background: p.bg, padding: port ? "5px 6px" : "8px 14px", cursor: p.cursor, touchAction: "pan-y" }}
                >
                  <div style={{ flex: "0 0 auto", width: port ? 22 : 34, height: port ? 22 : 34, borderRadius: "50%", display: "grid", placeItems: "center", background: p.avatarBg, color: p.avatarFg, fontWeight: 700, fontSize: port ? 9 : 12 }}>{p.avatar}</div>
                  <div style={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                    <div style={{ fontSize: port ? 11.5 : 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                      <span style={{ fontSize: port ? 9 : 10, fontWeight: 700, letterSpacing: "0.1em", color: p.posFg }}>{p.pos}</span>
                      <span style={{ fontSize: port ? 9 : 10, fontWeight: 700, color: "var(--color-neutral-600)", fontVariantNumeric: "tabular-nums" }}>#{p.num}</span>
                    </div>
                  </div>
                  <div style={{ flex: "0 0 auto", fontWeight: 800, fontSize: port ? 12 : 15, fontVariantNumeric: "tabular-nums", color: p.markFg }}>{p.mark}</div>
                </div>
              ))}

              {v.list.showCallUp && (
                <div style={{ padding: port ? "8px" : "12px 14px" }}>
                  <button onPointerDown={v.startPick} style={{ width: "100%", textAlign: "left", background: ink, color: bone, padding: port ? "8px" : "10px 12px", fontWeight: 700, fontSize: port ? 11 : 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>+ Call up players</button>
                </div>
              )}

              {v.list.showDone && (
                <div style={{ padding: port ? "8px" : "12px 14px" }}>
                  <button onPointerDown={v.endPick} style={{ width: "100%", textAlign: "left", background: ink, color: bone, padding: port ? "8px" : "10px 12px", fontWeight: 700, fontSize: port ? 11 : 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>Done</button>
                </div>
              )}

              {v.list.showAdd && (
                <div style={{ padding: port ? "8px" : "12px 14px" }}>
                  <button onPointerDown={v.actions.addPlayer} style={{ width: "100%", textAlign: "left", background: ink, color: bone, padding: port ? "8px" : "10px 12px", fontWeight: 700, fontSize: port ? 11 : 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>+ Add player</button>
                </div>
              )}

              {v.list.empty && (
                <div style={{ padding: port ? 8 : 14, fontSize: port ? 11.5 : 12.5, lineHeight: 1.6, color: "var(--color-neutral-700)" }}>{v.list.emptyText}</div>
              )}
            </div>

            {(!port || v.list.footer) && (
              <div style={{ padding: port ? "6px 8px" : "8px 14px", borderTop: "2px solid " + ink, fontSize: port ? 10 : 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--color-neutral-700)" }}>{v.list.footer}</div>
            )}
          </div>
        </div>

        {v.ghost.show && (
          <div style={{ position: "fixed", left: v.ghost.x, top: v.ghost.y, transform: "translate(-50%,-50%)", pointerEvents: "none", zIndex: 99, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ width: v.geom.token, height: v.geom.token, borderRadius: "50%", display: "grid", placeItems: "center", overflow: "hidden", background: v.ghost.bg, color: v.ghost.fg, fontWeight: 800, fontSize: v.geom.tokenFont, boxShadow: "0 12px 32px rgba(45,43,43,0.4)", transform: "scale(1.25)" }}>{v.ghost.badge}</div>
            <div style={{ marginTop: 4, padding: "1px 5px", background: ink, color: bone, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{v.ghost.label}</div>
          </div>
        )}

        {this.renderPasscode()}

        {v.oppGhost.show && (
          <div style={{ position: "fixed", left: v.oppGhost.x, top: v.oppGhost.y, transform: "translate(-50%,-50%) scale(1.25)", pointerEvents: "none", zIndex: 99, width: v.geom.oppToken, height: v.geom.oppToken, borderRadius: "50%", display: "grid", placeItems: "center", background: "var(--color-accent)", color: bone, fontWeight: 700, fontSize: v.geom.oppFont, fontVariantNumeric: "tabular-nums", boxShadow: "0 12px 32px rgba(45,43,43,0.4)" }}>{v.oppGhost.num}</div>
        )}
      </div>
    );
  }
}

function SoccerFormationRoute() {
  const { date } = useParams();
  const navigate = useNavigate();
  return <SoccerFormation date={date || null} onNavigate={d => navigate("/soccer/" + d)} />;
}

export default SoccerFormationRoute;
